import { StyleSheet, css } from 'aphrodite'
import React, { useState, useEffect, useRef, useCallback } from 'react'
import type { FC } from 'react'
import { Link } from 'react-router-dom'

const navItems = {
  Company: [
    { name: 'About DayDaCo', path: '/about', enabled: true },
    {
      name: 'Collaborative Governance',
      path: '/collaborative-governance',
      enabled: true,
    },
    { name: 'Digital Ecosystem', path: '/digital-ecosystem', enabled: true },
    { name: 'News and Press', path: '/news-press', enabled: true },
    { name: 'Careers', path: '/careers', enabled: true },
  ],
  Community: [
    { name: 'Community', path: '/community', enabled: true },
    {
      name: 'Sustainability',
      path: '/community/sustainability',
      enabled: true,
    },
    {
      name: 'Third-Party Engagements',
      path: '/community/third-party-engagements',
      enabled: true,
    },
  ],
  Support: [
    { name: 'Help Center', path: '#', enabled: false },
    { name: 'Contact Us', path: '/contact', enabled: true },
    { name: 'Tutorials', path: '#', enabled: false },
  ],
}

const LandingHeader: FC = () => {
  const [openMenu, setOpenMenu] = useState<string | null>(null)
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const headerRef = useRef<HTMLElement>(null)

  const handleToggle = (menu: string) => {
    setOpenMenu((prevMenu) => (prevMenu === menu ? null : menu))
  }

  const handleLinkClick = () => {
    window.scrollTo(0, 0)
    setOpenMenu(null)
    setIsMobileMenuOpen(false)
  }

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
    setOpenMenu(null)
  }

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      headerRef.current &&
      !headerRef.current.contains(event.target as Node)
    ) {
      setOpenMenu(null)
      setIsMobileMenuOpen(false)
    }
  }, [])

  const handleScroll = useCallback(() => {
    const scrollTop = window.scrollY
    setIsScrolled(scrollTop > 0)
  }, [])

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    window.addEventListener('scroll', handleScroll)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('scroll', handleScroll)
    }
  }, [handleClickOutside, handleScroll])

  return (
    <header
      className={css(styles.header, isScrolled && styles.headerScrolled)}
      ref={headerRef}
    >
      <div className={css(styles.headerContainer)}>
        <div className={css(styles.headerContent)}>
          <div className={css(styles.logoContainer)}>
            <Link to="/" onClick={handleLinkClick}>
              <img
                src="/dd_logo.png"
                alt="DayDaCo Logo"
                className={css(styles.logo)}
              />
            </Link>
          </div>
          <nav className={css(styles.nav)}>
            {Object.keys(navItems).map((title) => (
              <div key={title} className={css(styles.navItem)}>
                <button
                  className={css(styles.navButton)}
                  onClick={() => handleToggle(title)}
                >
                  <span className={css(styles.navLink)}>{title}</span>
                  <span className={css(styles.dropdownIcon)}>&#9662;</span>
                </button>
                {openMenu === title && (
                  <div className={css(styles.dropdown)}>
                    <ul className={css(styles.dropdownMenu)}>
                      {navItems[title as keyof typeof navItems].map((item) => (
                        <li key={item.name}>
                          {item.enabled ? (
                            <Link
                              to={item.path}
                              className={css(styles.dropdownLink)}
                              onClick={handleLinkClick}
                            >
                              {item.name}
                            </Link>
                          ) : (
                            <span className={css(styles.dropdownLinkDisabled)}>
                              {item.name}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
            <Link to="/login" onClick={handleLinkClick}>
              <button className={css(styles.loginButton)}>Login</button>
            </Link>
            <Link to="/register" onClick={handleLinkClick}>
              <button className={css(styles.signUpButton)}>Sign Up</button>
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className={css(styles.mobileMenuButton)}
            onClick={toggleMobileMenu}
          >
            <span
              className={css(
                styles.hamburgerLine,
                isMobileMenuOpen && styles.hamburgerLineActive1,
              )}
            />
            <span
              className={css(
                styles.hamburgerLine,
                isMobileMenuOpen && styles.hamburgerLineActive2,
              )}
            />
            <span
              className={css(
                styles.hamburgerLine,
                isMobileMenuOpen && styles.hamburgerLineActive3,
              )}
            />
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className={css(styles.mobileMenu)}>
            <nav className={css(styles.mobileNav)}>
              {Object.keys(navItems).map((title) => (
                <div key={title} className={css(styles.mobileNavSection)}>
                  <h4 className={css(styles.mobileNavTitle)}>{title}</h4>
                  <ul className={css(styles.mobileNavList)}>
                    {navItems[title as keyof typeof navItems].map((item) => (
                      <li key={item.name}>
                        {item.enabled ? (
                          <Link
                            to={item.path}
                            className={css(styles.mobileNavLink)}
                            onClick={handleLinkClick}
                          >
                            {item.name}
                          </Link>
                        ) : (
                          <span className={css(styles.mobileNavLinkDisabled)}>
                            {item.name}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              <div className={css(styles.mobileAuthButtons)}>
                <Link to="/login" onClick={handleLinkClick}>
                  <button className={css(styles.mobileLoginButton)}>
                    Login
                  </button>
                </Link>
                <Link to="/register" onClick={handleLinkClick}>
                  <button className={css(styles.mobileSignUpButton)}>
                    Sign Up
                  </button>
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  )
}

const styles = StyleSheet.create({
  header: {
    backgroundColor: '#ffffff',
    boxShadow:
      '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    position: 'sticky',
    top: 0,
    width: '100%',
    zIndex: 10,
    transition: 'background-color 0.3s ease',
  },
  headerScrolled: {
    backgroundColor: '#ffffff',
  },
  headerContainer: {
    margin: '0 auto',
    padding: '0 1rem',
    '@media (min-width: 768px)': {
      padding: '0 2rem',
    },
    '@media (min-width: 1024px)': {
      padding: '0 3rem',
    },
    '@media (min-width: 1280px)': {
      padding: '0 5rem',
    },
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 0',
    fontSize: '16px',
    fontWeight: 600,
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  logo: {
    height: '32px',
  },
  nav: {
    display: 'none',
    alignItems: 'center',
    gap: '32px',
    '@media (min-width: 768px)': {
      display: 'flex',
    },
  },
  navItem: {
    position: 'relative',
  },
  navButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  navLink: {
    color: '#374151',
    textDecoration: 'none',
    fontWeight: 600,
    fontSize: '16px',
    ':hover': {
      color: '#2563eb',
    },
  },
  dropdownIcon: {
    fontSize: '12px',
    color: '#374151',
  },
  loginButton: {
    backgroundColor: 'transparent',
    border: 'none',
    padding: 0,
    color: '#374151',
    borderRadius: '24px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 600,
    ':hover': {
      textDecoration: 'underline',
    },
  },
  signUpButton: {
    backgroundColor: '#6DC3FF',
    color: '#1F407E',
    padding: '8px 24px',
    borderRadius: '24px',
    border: 'none',
    ':hover': {
      textDecoration: 'underline',
    },
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow:
      '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
    padding: '8px 0',
    marginTop: '8px',
    minWidth: '200px',
    zIndex: 20,
  },
  dropdownMenu: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  dropdownLink: {
    display: 'block',
    padding: '8px 16px',
    color: '#374151',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    ':hover': {
      backgroundColor: '#f3f4f6',
      color: '#102E65',
    },
  },
  dropdownLinkDisabled: {
    display: 'block',
    padding: '8px 16px',
    color: '#9ca3af',
    cursor: 'default',
    whiteSpace: 'nowrap',
  },
  // Mobile styles
  mobileMenuButton: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '32px',
    height: '32px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    '@media (min-width: 768px)': {
      display: 'none',
    },
  },
  hamburgerLine: {
    width: '20px',
    height: '2px',
    backgroundColor: '#374151',
    margin: '2px 0',
    transition: 'all 0.3s ease',
    transformOrigin: 'center',
  },
  hamburgerLineActive1: {
    transform: 'rotate(45deg) translate(6px, 6px)',
  },
  hamburgerLineActive2: {
    opacity: 0,
  },
  hamburgerLineActive3: {
    transform: 'rotate(-45deg) translate(6px, -6px)',
  },
  mobileMenu: {
    display: 'block',
    backgroundColor: '#ffffff',
    borderTop: '1px solid #e5e7eb',
    '@media (min-width: 768px)': {
      display: 'none',
    },
  },
  mobileNav: {
    padding: '16px 0',
  },
  mobileNavSection: {
    marginBottom: '24px',
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  mobileNavTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#102E65',
    marginBottom: '12px',
    margin: 0,
  },
  mobileNavList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  mobileNavLink: {
    display: 'block',
    padding: '8px 0',
    color: '#4B5563',
    textDecoration: 'none',
    fontSize: '14px',
    ':hover': {
      color: '#102E65',
    },
  },
  mobileNavLinkDisabled: {
    display: 'block',
    padding: '8px 0',
    color: '#9ca3af',
    fontSize: '14px',
    cursor: 'default',
  },
  mobileAuthButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '16px',
    borderTop: '1px solid #e5e7eb',
  },
  mobileLoginButton: {
    backgroundColor: 'transparent',
    border: '1px solid #374151',
    padding: '12px 24px',
    color: '#374151',
    borderRadius: '24px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 600,
    textAlign: 'center',
  },
  mobileSignUpButton: {
    backgroundColor: '#6DC3FF',
    color: '#1F407E',
    padding: '12px 24px',
    borderRadius: '24px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 600,
    textAlign: 'center',
  },
})

export default LandingHeader
